/**
 * Error Boundary для отлова React ошибок
 * Оборачивает все приложение и показывает fallback UI при крахе
 */

import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { captureException } from '@/lib';

// Импортируем переводы напрямую, так как ErrorBoundary не может использовать хуки
import { errorBoundaryRu } from './config/i18n';
import { SUPPORT_EMAIL } from './config/constants';

// Переводы для fallback UI (класс-компонент не может использовать useTranslation)
const t = errorBoundaryRu;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Обновляем state чтобы показать fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Логируем в консоль (для dev)
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Отправляем в Sentry с контекстом componentStack (no-op в dev)
    captureException(error, {
      tags: { source: 'error-boundary' },
      extra: { componentStack: errorInfo.componentStack },
    });

    // Сохраняем детали ошибки в state для fallback UI
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = (): void => {
    // Сбрасываем состояние ошибки
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Если передан кастомный fallback - используем его
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Иначе показываем дефолтный UI
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center space-y-6">
            {/* Иконка */}
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-destructive" />
              </div>
            </div>

            {/* Заголовок */}
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-foreground">
                {t.title}
              </h1>
              <p className="text-muted-foreground">
                {t.description}
              </p>
            </div>

            {/* Детали ошибки (только в dev режиме) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="text-left">
                <summary className="text-sm text-muted-foreground active:text-foreground">
                  {t.technicalDetails}
                </summary>
                <div className="mt-2 p-4 bg-muted rounded-lg text-xs font-mono overflow-auto max-h-48">
                  <p className="text-destructive font-semibold mb-2">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo?.componentStack && (
                    <pre className="text-muted-foreground whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {/* Кнопки действий */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={this.handleReset}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t.tryAgain}
              </Button>

              <Button
                onClick={() => window.location.href = '/'}
                variant="outline"
                className="w-full"
              >
                {t.backToHome}
              </Button>
            </div>

            {/* Контакт поддержки */}
            <p className="text-sm text-muted-foreground">
              {t.supportText}{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-accent underline"
              >
                {t.contactSupport}
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}